/**
 * Content model integrated for the MyAccount Dashboard page
 *
 * @module views/components/SplitColumnContainer
 * @memberof -Common
 */
import './SplitColumnContainer.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import * as utils from './SplitColumnContainer';

/**
* Represents a SplitColumnContainer component
*
* @method
* @param { SplitColumnContainerProps} props - React properties passed from composition
* @returns SplitColumnContainer
*/
export const SplitColumnContainer = function( props ){

  const {
    componentKey,
    isBorderMiddle,
    isBorderTop,
    leftColumnModulesRef,
    modules,
    rightColumnModulesRef
  } = props;

  if( !hasItems( modules ) ){
    return null;
  }

  const singleItem = !hasItems( rightColumnModulesRef ) || !hasItems( leftColumnModulesRef );

  const leftModules = utils.renderModules( { componentKey: `${componentKey}-left`, modules, moduleRef: leftColumnModulesRef } );
  const rightModules = utils.renderModules( { componentKey: `${componentKey}-right`, modules, moduleRef: rightColumnModulesRef } );

  return (
    <div className={
      classNames(
        'SplitColumnContainer', {
          'SplitColumnContainer--singleItem': singleItem,
          'SplitColumnContainer--borderTopPadding': !isBorderTop
        }
      )
    }
    >
      { leftModules &&
      <div className={
        classNames(
          'SplitColumnContainer__LeftReference', {
            'SplitColumnContainer__LeftReference--isBorderMiddle': !singleItem && isBorderMiddle,
            'SplitColumnContainer__LeftReference--singleItem': singleItem,
            'SplitColumnContainer__LeftReference--borderTop': isBorderTop,
            'SplitColumnContainer__LeftReference--noPadding': !isBorderTop
          }
        )
      }
      >
        { leftModules }
      </div>
      }

      { rightModules &&
      <div className={
        classNames(
          'SplitColumnContainer__RightReference', {
            'SplitColumnContainer__LeftReference--borderTop': isBorderTop,
            'SplitColumnContainer__RightReference--noPadding': !isBorderTop,
            'SplitColumnContainer__RightReference--singleItem': singleItem,
            'SplitColumnContainer__RightReference--borderTopPadding': !isBorderTop
          }
        )
      }
      >
        { rightModules }
      </div>
      }
    </div>
  );
};

/**
 * renderModules function for mapping and rendering the modules
 * @method
 * @param {Object} data - Passing modules object as an argument
 * @param {object} methods - Passing the leftRef and rightRef as arguments,
 * @property {string} componentKey - Sets the componentKey
 */
export const renderModules = ( data ) => {
  const { modules, componentKey, moduleRef } = data || {};

  if( !hasItems( moduleRef ) || !hasItems( modules ) ){
    return null;
  }

  return moduleRef.map( ( id, index ) => {
    const module = modules.find( ( el ) => el.id === id );
    return (
      <AsyncComponent key={ `${componentKey}-col-${index}-${id}` }
        { ...spreadProps( { ...module } ) }
      />
    );
  } );
};

/**
* Property type definitions
* @typedef SplitColumnContainerProps
* @property {array} modules - Sets an array of components to be passed in the two columns through the AsyncComponent.
* @property {boolean} hasModules - Sets the boolean value to true if and only two components are present in modules array.
* @property {array} leftColumnModulesRef - Sets the ID of the modules to be placed in the left container
* @property {array} rightColumnModulesRef - Sets the ID of the modules to be placed in the right container
* @property {boolean} isBorderMiddle - Sets the middle border as a divider.
* @property {boolean} isBorderTop - Sets the top border as a divider.
*/
export const propTypes =  {
  modules: PropTypes.array,
  hasModules: PropTypes.bool,
  leftColumnModulesRef: PropTypes.array,
  rightColumnModulesRef: PropTypes.array,
  isBorderMiddle: PropTypes.bool,
  isBorderTop: PropTypes.bool
};
SplitColumnContainer.propTypes = propTypes;
export default SplitColumnContainer;